import cx from 'clsx';
import React, { useEffect, useState } from 'react';

import DeviceProvider from '@/components/DeviceProvider';
import { LaunchStrategiesProvider } from '@/components/LaunchStrategiesProvider';
import { UserSessionProvider } from '@/components/UserSessionProvider';
import config from '@/config';
import { useHeaderHeight } from '@/hooks/useHeaderHeight';
import { CallbackMode, DeviceOs } from '@/logic/main/types';
import { GameLaunchStrategies } from '@/types/gameTypes';

import styles from './Layout.module.scss';

interface LayoutPropTypes {
  children?: React.ReactNode;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  deviceOs: DeviceOs;
  launchStrategies: GameLaunchStrategies;
  deviceNative: boolean;
  gameModalOpen?: boolean;
  callbackMode: CallbackMode;
  loggedIn: boolean;
}

const Layout: React.FC<LayoutPropTypes> = ({
  children,
  deviceType,
  deviceOs,
  launchStrategies,
  deviceNative,
  gameModalOpen,
  loggedIn,
}) => {
  const mode = config.staticComponents['Layout']?.mode || 'default';
  const [headerElement, setHeaderElement] = useState<HTMLDivElement | null>(
    null
  );

  useEffect(() => {
    const layoutElement = document.getElementById('layout');
    if (layoutElement) {
      setHeaderElement(layoutElement as HTMLDivElement);
      layoutElement.className = cx(styles.layout, {
        [styles.layoutWithNavigation]: mode === 'small',
        [styles.layoutWithFooter]: mode === 'small',
        [styles.overflowHidden]: gameModalOpen,
      });
    }

    const rootContent = document.getElementById('react-root');
    if (rootContent) {
      rootContent.className = cx(styles.rootContent, {
        [styles.contentResponsive]: mode === 'default',
        [styles.contentStatic]: mode !== 'default',
      });
    }

    const pageContent = document.getElementById('page-content');
    if (pageContent) {
      pageContent.className = cx(styles.content);
    }
  }, [mode, gameModalOpen]);

  const headerExtraMargin = useHeaderExtraMargin(headerElement);

  useEffect(() => {
    if (headerElement) {
      headerElement.style.marginTop = `${headerExtraMargin}px`;
    }
  }, [headerElement, headerExtraMargin]);

  return (
    <UserSessionProvider loggedIn={loggedIn}>
      <LaunchStrategiesProvider
        launchStrategies={launchStrategies}
        deviceType={deviceType}
        loggedIn={loggedIn}
      >
        <DeviceProvider
          deviceType={deviceType}
          deviceOs={deviceOs}
          deviceNative={deviceNative}
        >
          {children}
        </DeviceProvider>
      </LaunchStrategiesProvider>
    </UserSessionProvider>
  );
};

const useHeaderExtraMargin = (layoutElement: HTMLDivElement | null) => {
  const getLayoutPadding = (layoutElement: HTMLDivElement) => {
    if (!getComputedStyle) return 0;
    const padding = getComputedStyle(layoutElement).paddingTop;
    const number = Number(padding?.replace('px', ''));
    return isNaN(number) ? 0 : number;
  };

  const headerHeight = useHeaderHeight();
  const currentHeaderOffset = layoutElement
    ? getLayoutPadding(layoutElement)
    : headerHeight;
  return Math.max(0, headerHeight - currentHeaderOffset);
};

export default Layout;
